import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Button,
  Paper,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  MobileStepper
} from '@mui/material';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@mui/material/styles';

const RecentProjects = () => {
  const theme = useTheme();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep + 1) % projects.length);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => 
      prevActiveStep === 0 ? projects.length - 1 : prevActiveStep - 1
    );
  };

  useEffect(() => {
    // Simulate API call to fetch projects
    const fetchProjects = async () => {
      setLoading(true);
      try {
        // Initialize with empty array - to be populated from API in the future
        setProjects([]);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'primary';
      case 'Planning':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ mb: 3, borderRadius: 2, p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <AgricultureIcon sx={{ mr: 1, color: 'primary.main' }} />
        Recent Projects
      </Typography>
      
      {projects.length > 0 && (
        <Box sx={{ maxWidth: '100%', flexGrow: 1 }}>
          <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <CardMedia
              component="img"
              height="200"
              image={projects[activeStep].imageUrl}
              alt={projects[activeStep].title}
              sx={{ objectFit: 'cover' }}
            />
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" component="div">
                  {projects[activeStep].title}
                </Typography>
                <Chip 
                  label={projects[activeStep].status} 
                  color={getStatusColor(projects[activeStep].status)} 
                  size="small" 
                />
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {projects[activeStep].description}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarMonthIcon fontSize="small" sx={{ mr: 0.5 }} />
                  {formatDate(projects[activeStep].date)}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                  {projects[activeStep].location}
                </Box>
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center' }}>
              <Button size="small" color="primary">
                View Details
              </Button>
            </CardActions>
          </Card>
          
          <MobileStepper
            steps={projects.length}
            position="static"
            activeStep={activeStep}
            sx={{ mt: 2, bgcolor: 'background.paper' }}
            nextButton={
              <Button
                size="small"
                onClick={handleNext}
              >
                Next
                {theme.direction === 'rtl' ? (
                  <KeyboardArrowLeft />
                ) : (
                  <KeyboardArrowRight />
                )}
              </Button>
            }
            backButton={
              <Button size="small" onClick={handleBack}>
                {theme.direction === 'rtl' ? (
                  <KeyboardArrowRight />
                ) : (
                  <KeyboardArrowLeft />
                )}
                Back
              </Button>
            }
          />
        </Box>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button variant="outlined" color="primary">
          View All Projects
        </Button>
      </Box>
    </Paper>
  );
};

export default RecentProjects; 